// fx 'bevel' — live bevel/emboss over ANY raster's alpha.
//
// The shading core is a live-filter port of embossRecipe.js's shadeLayer
// (lib/emboss/embossRecipe.js is the SOURCE OF TRUTH for the bevel math:
// alpha → exact-EDT height field with a plateau profile → apparent-height
// normals → FACE-RELATIVE lighting (the flat face keeps the fill's exact
// color; rims light/darken by how far their normal swings from flat) →
// highlight screen + shadow multiply + gloss bloom). It is deliberately
// COPIED, not imported: embossRecipe's baked output is byte-identity-guarded
// (hard-coded sha256s in the sticker-bevel probe) and its module is kept out
// of the editor boot chunk — importing it here would change its execution
// context for zero gain. Differences from the bake, on purpose:
//   - Everything is computed in DEVICE px per recipe unit (unitPx), so the
//     look is identical at any zoom, and the fast half-resolution pass
//     matches the full-quality upgrade. The model is resolution-independent
//     by construction (apparent height A and band both scale with unitPx).
//   - A CONTACT SHADOW outside the glyph (the bake's grounding pass): the
//     blurred silhouette sampled with an offset toward the light, so a soft
//     dark edge hugs the letters on the side away from the sun. That is what
//     the spill margin exists for.
//   - 2026-07-19 (light-shape-finish lane): technique 'chisel' is LIVE — the
//     designed one-line profile swap over the shared EDT (linear t = straight
//     facets + the mitre ridge, exactly embossRecipe's chisel law), measured
//     comfortably inside the sync budget (the linear profile is cheaper than
//     the quarter-round). 'size' (the band width) is now surfaced in the
//     sidebar as "Bevel width" (0-100), and the LIVE mapping gained the v3
//     WIDTH-SATURATION law: the effective band clamps at the glyph's own max
//     interior distance, so width 100 reliably reads as a FULL DOME (balloon)
//     on any font instead of a shading-saturated cone — see the note at the
//     clamp in shadeBevel. Live-only by design: the baked engine keeps its
//     unclamped law (byte-guarded; presets never reach the clamped regime).
//   - lightMode 'poster' (default 'custom'): shading azimuth/altitude derive
//     from the document's shared posterLight (fxPosterLight.js) instead of
//     params.angle + the hidden ALTITUDE_DEG. The seam's hash appends the
//     poster values for opted-in stacks, so a light edit re-renders exactly
//     the opted-in objects through the existing machinery.
//
// applySync runs the pipeline at HALF the offscreen resolution and upscales
// (soft for a beat — the Photoshop feel); applyAsync reruns it at full
// resolution and the seam stashes that result. The exact EDT fits the sync
// budget at half resolution (~a few ms at editor sizes — measured in
// probe-bevelquality; chisel re-measured 2026-07-19 in verify-lightshape).
//
// Composition with fx 'extrude' (owner 2026-07-16): extrude declares
// consumes:['bevel'] — while a live 3D bake is active the seam never runs
// this filter (the 2D shading must not double-hit the 3D raster); the
// extrude bake reads THIS step's depth and cuts it into the geometry as
// edge rounding instead. The step and its params stay in data.fx untouched,
// so extrude back at 0 returns this exact live bevel. Nothing to declare on
// this side — the consumer owns the mapping (see fxExtrude.js).

import { registerFx } from './fxRegistry';
import { getPosterLightFor } from './fxPosterLight';

export const FX_BEVEL_DEFAULTS = {
  depth: 50, // steepness / apparent height (0 removes the step — see isEmpty)
  size: 34, // bevel band width ("Bevel width" in the sidebar), recipe units
  soften: 12, // rounds the shoulder (never widens the band)
  angle: 135, // light azimuth, degrees (0 = right, CCW positive)
  highlight: 90, // highlight strength 0-100
  shadow: 78, // shadow strength 0-100
  technique: 'smooth', // 'smooth' (Rounded) | 'chisel' (Chiseled) — live since 2026-07-19
  lightMode: 'custom', // 'custom' (params.angle) | 'poster' (shared posterLight)
  finish: 'satin', // surface family — see FINISHES ('satin' = the pre-finish law)
};

const ALTITUDE_DEG = 52; // emboss's flattering hidden default
const BAND_K = 0.16; // band px per (size · unit) — embossRecipe's calibration
const AMBIENT = 0.14; // fill light in the shadow bands (never pitch black)
const SHADE_ALT_K = 0.58; // diffuse terms shade against alt × this (≈30° at
// the stored 52°): keeps light-facing flanks luminous at any steepness — see
// embossRecipe.js SHADE_ALT_K for the geometry.
const DOME_SMOOTH_K = 0.30; // dome law (v4): Rounded's height field is blurred
// at σ = this × the effective band, AFTER the profile. Tuned by LOOKING at
// bold rounded-font renders (0.2 left faint spine ghosts at width 50; 0.4
// visibly deflated the rim contrast): 0.30 erases the medial-axis crest and
// junction mitre lines at any width while the silhouette edge keeps its
// steepness. σ ∝ band = the reconciliation with the v3 plateau law: at low
// width σ is a couple px — the plateau face stays exactly flat (paint
// untouched) behind a softly rounded shoulder; as width grows the shrinking
// plateau melts into the shoulder blur (no flat patch reappears mid-range);
// at saturation the whole letter is one continuous dome.

const clamp = (v, min, max, fb) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fb;
  return Math.min(max, Math.max(min, n));
};

// FINISH surface laws (owner 2026-07-20: "finish settings for the sticker and
// its bevels"). NO invented constants — every value is anchored to the BAKED
// engine's archetype style bundles (lib/emboss/embossPresets.js
// EMBOSS_STYLE_PRESETS) and its shading defaults (embossRecipe.js
// EMBOSS_DEFAULTS), the provenance being the whole correctness argument in a
// no-visual-verification lane. Each finish supplies the specular gloss and a
// scale on the caller's highlight/shadow opacities plus a shoulder-soften
// delta; the caller keeps ownership of angle/technique/highlight&shadow base.
//   gloss    → glossAmt = gloss/100 (drives specExp 8→44 and the bloom).
//   hiK/shK  → multiply the caller's highlight/shadow opacity (clamped ≤1 so a
//              boosted term can't invert the screen/multiply).
//   softenAdd→ added to the caller's Soften (floored at 0).
const FINISHES = {
  // satin = the pre-finish fixed law, BYTE-IDENTICAL: gloss 30 is the former
  // module GLOSS const == embossRecipe EMBOSS_DEFAULTS.gloss (embossRecipe.js
  // line 132); no highlight/shadow scale, no extra soften.
  satin: { gloss: 30, hiK: 1, shK: 1, softenAdd: 0 },
  // matte = no specular (gloss 0 — the flat floor of the baked low-gloss
  // 'carved'/'soft' styles, embossPresets.js lines 21-22 gloss 20/12) with a
  // softened highlight (×0.7) and a wider shoulder (+18 soften, the baked
  // 'soft' soften 34 regime, embossPresets.js line 22).
  matte: { gloss: 0, hiK: 0.7, shK: 1, softenAdd: 18 },
  // glossy = the bake's high-gloss regime BELOW metal: gloss 62 (between the
  // 'Raised Gold' preset's 45 @ hl 88, embossPresets.js lines 93-95, and the
  // foil band 80-88) with a boosted highlight (×1.1); smooth, no extra soften.
  glossy: { gloss: 62, hiK: 1.1, shK: 1, softenAdd: 0 },
  // metal = the baked metal law: max gloss 85 (embossPresets 'metal' gloss 80
  // line 27 ∪ the sparkle-foil presets gloss 82-88, lines 62/107/127), a hot
  // highlight (×1.12) against a firm shadow (×1.12) — the foil hl 92-95 / sh
  // 76-84 contrast (embossPresets.js lines 36/63/108) — and a tightened
  // shoulder (−4, the metal preset soften 8, line 27). Technique stays the
  // caller's: finish is SURFACE, not the chisel geometry the preset also uses.
  metal: { gloss: 85, hiK: 1.12, shK: 1.12, softenAdd: -4 },
};

// v3 width-saturation dome memory: live object → its full-raster max interior
// distance in RECIPE UNITS (see the note in runBevel). WeakMap on purpose —
// no instance props (removeFxRender's clean-detach contract holds), entries
// die with the object.
const DOME_MEMORY = new WeakMap();

function sanitize(p) {
  const src = p || {};
  return {
    // depth/size run to 250 (owner 2026-07-19, "we are working on large
    // files"): /100 in the shading math is the calibration point, not a
    // ceiling — big text needs bands and amplitudes past the old max. The
    // width-saturation clamp keeps oversized bands physical per glyph.
    depth: clamp(src.depth, 0, 250, FX_BEVEL_DEFAULTS.depth),
    // Floor 2 → 0 (2026-07-19): the UI slider runs 0-100; sub-2 bands render
    // as a hairline shoulder (bandPx floors at 1 device px), and no saved
    // design can carry <2 (the old clamp made it unrepresentable).
    size: clamp(src.size, 0, 250, FX_BEVEL_DEFAULTS.size),
    soften: clamp(src.soften, 0, 100, FX_BEVEL_DEFAULTS.soften),
    angle: clamp(src.angle, 0, 360, FX_BEVEL_DEFAULTS.angle),
    highlight: clamp(src.highlight, 0, 100, FX_BEVEL_DEFAULTS.highlight),
    shadow: clamp(src.shadow, 0, 100, FX_BEVEL_DEFAULTS.shadow),
    technique: src.technique === 'chisel' ? 'chisel' : 'smooth',
    lightMode: src.lightMode === 'poster' ? 'poster' : 'custom',
    // FINISH (owner 2026-07-20): surface family (see FINISHES). Default 'satin'
    // = the pre-finish law, so every saved bevel sanitizes byte-identically in
    // render (a new key is appended, but satin's constants are unchanged).
    finish: FINISHES[src.finish] ? src.finish : 'satin',
  };
}

/** Object-unit margin the effect may reach past the tight box: the bevel band
 * itself is INSIDE the glyph — only the contact shadow escapes (blur σ·3 +
 * light-driven offset). */
function spill(p, u) {
  if (!(p.depth > 0)) return 0;
  return (9 + 0.26 * p.size) * u + 2;
}

// --- exact Euclidean distance transform (Felzenszwalb–Huttenlocher) --------
// Copied from embossRecipe.js (see the copy-not-import note above).
const EDT_INF = 1e10;

function edt1d(f, d, v, z, n) {
  let k = 0;
  v[0] = 0;
  z[0] = -Infinity;
  z[1] = Infinity;
  for (let q = 1; q < n; q++) {
    let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    while (k > 0 && s <= z[k]) {
      k--;
      s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]);
    }
    k++;
    v[k] = q;
    z[k] = s;
    z[k + 1] = Infinity;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dq = q - v[k];
    d[q] = dq * dq + f[v[k]];
  }
}

function edt2d(f, W, H) {
  const n = Math.max(W, H);
  const buf = new Float64Array(n);
  const d = new Float64Array(n);
  const v = new Int32Array(n);
  const z = new Float64Array(n + 1);
  for (let x = 0; x < W; x++) {
    for (let y = 0; y < H; y++) buf[y] = f[y * W + x];
    edt1d(buf, d, v, z, H);
    for (let y = 0; y < H; y++) f[y * W + x] = d[y];
  }
  for (let y = 0; y < H; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) buf[x] = f[row + x];
    edt1d(buf, d, v, z, W);
    for (let x = 0; x < W; x++) f[row + x] = d[x];
  }
  return f;
}

/** Squared distance (px²) to the glyph edge, inside only; sub-pixel seeded
 * from the anti-aliased coverage. */
function innerDistSq(rgba, W, H) {
  const f = new Float64Array(W * H);
  for (let i = 0, a = 3; i < W * H; i++, a += 4) {
    const cov = rgba[a] / 255;
    if (cov <= 0.5) f[i] = 0;
    else if (cov >= 0.999) f[i] = EDT_INF;
    else { const s = cov - 0.5; f[i] = s * s; }
  }
  return edt2d(f, W, H);
}

/** Same seed law over a stride-1 alpha array (a LAYER mask — the glyph-only
 * variant, a sticker band, an analytic badge silhouette). */
function innerDistSqAlpha(alpha, W, H) {
  const f = new Float64Array(W * H);
  for (let i = 0; i < W * H; i++) {
    const cov = alpha[i] / 255;
    if (cov <= 0.5) f[i] = 0;
    else if (cov >= 0.999) f[i] = EDT_INF;
    else { const s = cov - 0.5; f[i] = s * s; }
  }
  return edt2d(f, W, H);
}

/** Small float Gaussian (clamp-to-edge) — the 0.6px ripple-killing floor and
 * the Soften rounding. Working σ stays small (≤ band·0.45), so the plain
 * kernel is cheap enough for the sync pass. */
function blurField(field, W, H, sigma) {
  if (sigma > 8) return boxBlurField(field, W, H, sigma); // kernel cap ≈ 24px radius ≈ 3σ at 8

  if (!(sigma > 0.05)) return;
  const radius = Math.max(1, Math.min(24, Math.ceil(sigma * 3)));
  const k = new Float32Array(radius * 2 + 1);
  const s2 = 2 * sigma * sigma;
  let sum = 0;
  for (let i = -radius; i <= radius; i++) { const v = Math.exp(-(i * i) / s2); k[i + radius] = v; sum += v; }
  for (let i = 0; i < k.length; i++) k[i] /= sum;
  const tmp = new Float32Array(W * H);
  for (let y = 0; y < H; y++) {
    const row = y * W;
    for (let x = 0; x < W; x++) {
      let a = 0;
      for (let i = -radius; i <= radius; i++) {
        let xx = x + i; if (xx < 0) xx = 0; else if (xx >= W) xx = W - 1;
        a += field[row + xx] * k[i + radius];
      }
      tmp[row + x] = a;
    }
  }
  for (let x = 0; x < W; x++) {
    for (let y = 0; y < H; y++) {
      let a = 0;
      for (let i = -radius; i <= radius; i++) {
        let yy = y + i; if (yy < 0) yy = 0; else if (yy >= H) yy = H - 1;
        a += tmp[yy * W + x] * k[i + radius];
      }
      field[y * W + x] = a;
    }
  }
}

// --- large-σ height blur: 3 iterated box passes (Wells' boxes-for-Gauss) ---
// The dome law needs σ ∝ band — 30+ device px on print-size bakes, far past
// blurField's radius-24 kernel cap (which silently truncates ≈ σ>8). Box
// passes are O(px) at ANY σ (running sums), clamp-to-edge like blurField,
// and 3 iterations are within ~3% of a true Gaussian — invisible under the
// Sobel. Same cost at half-res sync and full-res async, so the two passes
// keep agreeing.
function boxPassH(src, dst, W, H, r) {
  const norm = 1 / (2 * r + 1);
  for (let y = 0; y < H; y++) {
    const row = y * W;
    let acc = src[row] * (r + 1);
    for (let i = 1; i <= r; i++) acc += src[row + Math.min(W - 1, i)];
    for (let x = 0; x < W; x++) {
      dst[row + x] = acc * norm;
      acc += src[row + Math.min(W - 1, x + r + 1)] - src[row + Math.max(0, x - r)];
    }
  }
}

function boxPassV(src, dst, W, H, r) {
  const norm = 1 / (2 * r + 1);
  for (let x = 0; x < W; x++) {
    let acc = src[x] * (r + 1);
    for (let i = 1; i <= r; i++) acc += src[Math.min(H - 1, i) * W + x];
    for (let y = 0; y < H; y++) {
      dst[y * W + x] = acc * norm;
      acc += src[Math.min(H - 1, y + r + 1) * W + x] - src[Math.max(0, y - r) * W + x];
    }
  }
}

function boxBlurField(field, W, H, sigma) {
  if (!(sigma > 0.05)) return;
  // Ideal box widths for 3 passes approximating Gaussian σ.
  const wIdeal = Math.sqrt((12 * sigma * sigma) / 3 + 1);
  let wl = Math.floor(wIdeal);
  if (wl % 2 === 0) wl -= 1;
  const wu = wl + 2;
  const mIdeal = (12 * sigma * sigma - 3 * wl * wl - 12 * wl - 9) / (-4 * wl - 4);
  const m = Math.round(mIdeal);
  const tmp = new Float32Array(W * H);
  for (let i = 0; i < 3; i++) {
    const r = (((i < m ? wl : wu) - 1) / 2) | 0;
    if (r < 1) continue;
    boxPassH(field, tmp, W, H, r);
    boxPassV(tmp, field, W, H, r);
  }
}

/**
 * The pipeline. `src` holds the base render at device resolution in its
 * top-left env.width × env.height region (pooled canvases can be larger).
 * resFactor < 1 = work at reduced resolution and upscale back into src.
 */
function runBevel(src, env, resFactor) {
  const p = env.params;
  const W0 = env.width;
  const H0 = env.height;
  const W = Math.max(8, Math.round(W0 * resFactor));
  const H = Math.max(8, Math.round(H0 * resFactor));
  const fx = W / W0; // actual factor after rounding
  const fy = H / H0;
  // Device px per recipe unit at WORKING resolution — every distance in the
  // model is × unitPx, so the half-res pass and the full-res upgrade agree.
  const unitPx = Math.max(0.02, env.u * env.scale * fx);

  // LAYER SEPARATION (owner 2026-07-20): when a sticker/outline band is
  // active the seam offers env.renderVariant('noStroke') — a second,
  // stroke-suppressed base pass. Height, shading AND the contact silhouette
  // then derive from that glyph-only raster: the LETTERS dome, the sticker
  // layer beneath stays flat (its own dome is the sticker step's `beveled`),
  // and the letters' contact shadow grounds them ON the sticker layer.
  // Cost, honestly: one extra vector base render + one W×H drawImage +
  // getImageData per bevel run — small next to the exact EDT at the same
  // W×H, and it runs ONLY when a band effect is live (the seam gates the
  // hook; plain text keeps the single-pass path bit-for-bit).
  // Failure posture: any variant hiccup falls back to the composite mask —
  // the pre-lane look, never a broken render.
  let maskA = null;
  let silCanvas = src;
  if (typeof env.renderVariant === 'function') {
    try {
      const v = env.renderVariant('noStroke');
      if (v && v.canvas) {
        silCanvas = v.canvas;
        const mScratch = env.getScratch(W, H);
        mScratch.ctx.drawImage(v.canvas, 0, 0, W0, H0, 0, 0, W, H);
        const mImg = mScratch.ctx.getImageData(0, 0, W, H);
        maskA = new Uint8ClampedArray(W * H);
        const md = mImg.data;
        for (let i = 0; i < W * H; i++) maskA[i] = md[i * 4 + 3];
      }
    } catch (_) { maskA = null; silCanvas = src; }
  }

  // Contact-shadow source: blur(silhouette) via one native filtered
  // drawImage; only its ALPHA is read. Layered: the GLYPH silhouette — the
  // letters cast the shadow, never the band/badge beneath them.
  const sigmaC = (2.5 + p.size * 0.06) * unitPx;
  const blur = env.getScratch(W, H);
  blur.ctx.filter = `blur(${sigmaC.toFixed(2)}px)`;
  blur.ctx.drawImage(silCanvas, 0, 0, W0, H0, 0, 0, W, H);
  blur.ctx.filter = 'none';
  const cImg = blur.ctx.getImageData(0, 0, W, H);

  // Working-resolution base pixels.
  let work = null;
  let baseImg;
  if (fx === 1 && fy === 1) {
    baseImg = src.getContext('2d').getImageData(0, 0, W, H);
  } else {
    work = env.getScratch(W, H);
    work.ctx.drawImage(src, 0, 0, W0, H0, 0, 0, W, H);
    baseImg = work.ctx.getImageData(0, 0, W, H);
  }

  // Light source: params by default; the document's shared sun when this
  // step opted in (lightMode 'poster'). Resolved at RUN time off the live
  // object's canvas — the seam's hash carries the poster values for opted-in
  // stacks, so a poster-light edit re-runs this with the new sun while
  // custom-mode objects keep their stash. env.obj may be absent (detached
  // probe envs) — getPosterLightFor falls back to the defaults.
  const poster = p.lightMode === 'poster' ? getPosterLightFor(env.obj) : null;
  const light = poster
    ? { azDeg: poster.angle, altDeg: poster.elevation }
    : { azDeg: p.angle, altDeg: ALTITUDE_DEG };

  // Width-saturation dome memory (viewport-patch coherence): the v3 clamp
  // rides the raster's max interior distance — but a PATCH bake sees only a
  // SLICE of the glyphs, whose own max distance can be smaller, and a
  // slice-computed clamp would shade a visible band-width jump at the patch
  // border. Full-object renders therefore REMEMBER their measured distance
  // in RECIPE UNITS (resolution-independent — the half-res sync pass and the
  // full-res async pass measure the same value within sub-px), and region
  // renders REUSE it. WeakMap keyed by the live object: no instance props
  // (removeFxRender's "indistinguishable afterwards" contract holds), GC
  // rides the object. A patch racing a content edit may reuse the previous
  // content's clamp for one generation — the next full bake (already
  // scheduled by the same edit) rewrites it and the evaluator re-patches.
  const dome = { clampPx: 0, measuredPx: 0 };
  if (env.patchRegion && env.obj) {
    const rememberedU = DOME_MEMORY.get(env.obj);
    if (rememberedU > 0) dome.clampPx = rememberedU * unitPx;
  }

  shadeBevel(baseImg, cImg, W, H, p, unitPx, light, dome,
    maskA ? { mask: maskA, under: 'shadow' } : null);

  if (!env.patchRegion && env.obj && dome.measuredPx > 0) {
    DOME_MEMORY.set(env.obj, dome.measuredPx / unitPx);
  }

  const srcCtx = src.getContext('2d');
  if (fx === 1 && fy === 1) {
    srcCtx.putImageData(baseImg, 0, 0);
  } else {
    work.ctx.putImageData(baseImg, 0, 0);
    srcCtx.clearRect(0, 0, W0, H0);
    // The upscale is the "soft for a beat" moment; the async pass replaces it.
    srcCtx.drawImage(work.canvas, 0, 0, W, H, 0, 0, W0, H0);
  }
  return src;
}

/**
 * Shade in place — embossRecipe.js shadeLayer's exact v3 model (raised
 * style, white/black lights) plus the outside contact shadow. Two profiles
 * over the SAME exact-EDT distance (the shared-source law):
 *   'smooth' — quarter-round shoulder 1−(1−t)², easing into the plateau;
 *   'chisel' — linear t: constant normal per flank (a true facet) and a
 *   mitre ridge where opposing ramps collide. NO shoulder curve — bending
 *   the ramp would dissolve the facet back into a smooth bevel. (The soften
 *   blur below still runs: its 0.6px floor kills the EDT grid ripple the
 *   Sobel would amplify, exactly as in the baked engine.)
 * @param {ImageData} img  base raster (rgb = surface, alpha = mask), mutated.
 * @param {ImageData|null} cSrc blurred silhouette (alpha read) — of the MASK
 *   layer when `layer` is given. null = no contact shadow anywhere (legal
 *   only with layer.under === 'none').
 * @param {{azDeg:number, altDeg:number}} light resolved sun (params or poster).
 * @param {{clampPx:number, measuredPx:number}} [dome] width-saturation memory
 *   (see runBevel): clampPx > 0 = REUSE this max-distance (region renders);
 *   else the measured value is reported back through measuredPx.
 * @param {{mask:Uint8ClampedArray|Uint8Array,
 *          heightMask?:Uint8ClampedArray|Uint8Array, under:'shadow'|'none'}} [layer]
 *   LAYER MODE (layer-separation lane, 2026-07-20): height and shading come
 *   from `mask` (stride-1 alpha, W×H) instead of the raster's own alpha, and
 *   ONLY mask-covered pixels are shaded — everything else keeps its paint.
 *   `heightMask` (edge-law extension, owner 2026-07-20) optionally splits the
 *   height SOURCE from the paint mask: the EDT/height field is built from it
 *   while shading is still gated to `mask`, so a plate can dome its filled
 *   outer silhouette yet paint a subset. Omitted ⇒ heightMask = mask (every
 *   pre-extension caller is byte-identical).
 *   under 'shadow' = base ink outside the mask sits BENEATH the layer
 *   (sticker halo/badge under domed letters): it receives the layer's contact
 *   shadow as a multiply, and fully-transparent pixels still get the classic
 *   outside write. under 'none' = whatever isn't the mask is ABOVE or absent
 *   (letters over a domed band/badge): no contact, no outside writes — the
 *   caller's own drop shadow grounds the layer. layer == null is BYTE-
 *   IDENTICAL to the pre-lane single-layer path.
 */
function shadeBevel(img, cSrc, W, H, p, unitPx, light, dome, layer = null) {
  const bd = img.data;
  const cd = cSrc ? cSrc.data : null;
  const mask = layer ? layer.mask : null;
  // HEIGHT SOURCE ≠ PAINT MASK (owner 2026-07-20 edge law): a caller may drive
  // the height field from a DIFFERENT alpha than the pixels it paints. The
  // sticker plate domes its FILLED outer silhouette (which has ONLY outer
  // edges) while a legacy band caller could paint just a ring — so rims rise
  // at the plate's outer die-cut edge and read flat where the plate meets the
  // text, exactly how the baked border layer has always shaded (embossRecipe
  // shadeLayer runs on the filled fill+stroke silhouette). Defaults to `mask`,
  // so every pre-edge-law caller (plain bevel, shape-mode badge, drawBadge's
  // solid plate) is byte-identical.
  const heightMask = layer && layer.heightMask ? layer.heightMask : mask;
  const under = layer ? layer.under : 'shadow';
  // FINISH surface law (owner 2026-07-20): resolved once — used by the shoulder
  // soften below and the specular/highlight/shadow terms further down. satin =
  // the pre-finish constants → byte-identical.
  const fin = FINISHES[p.finish] || FINISHES.satin;

  // Height field: exact EDT → per-technique profile with a FLAT plateau
  // (which vanishes when the band outgrows the stroke — the balloon read).
  const dsq = heightMask ? innerDistSqAlpha(heightMask, W, H) : innerDistSq(bd, W, H);
  const paramBandPx = Math.max(1, p.size * BAND_K * unitPx);
  // WIDTH-SATURATION LAW (v3, 2026-07-19 — the owner's "completely round
  // text if we set the bevel width high enough"): the effective band CLAMPS
  // at the glyph's own deepest interior distance. Un-clamped, a band wider
  // than the strokes leaves every pixel in the profile's near-linear low-t
  // region — geometrically a CONE, and the lowered-sun shading saturates on
  // its steep flanks, so "maximum roundness" read as facets (measured on the
  // first screenshot pass of the light-shape-finish lane). Clamped, width
  // 100 spans t 0→1 across every stroke on ANY font: the quarter-round's
  // crest (slope → 0) lands on the spine and the letters read as true domes
  // — Rounded = balloon, Chiseled = full pyramid ridge. Below the clamp
  // (band < the glyph's max distance — every pre-lane saved design: the UI
  // never exposed size, so they all carry the default 34) the mapping is
  // BYTE-IDENTICAL to v2; the baked engine keeps its unclamped law (byte-
  // guarded), so explorer bakes and live agree everywhere presets live and
  // diverge only in the new top-end regime the bake never reaches.
  let maxDistPx;
  if (dome && dome.clampPx > 0) {
    // Region render — reuse the full-raster measurement (patch coherence).
    maxDistPx = dome.clampPx;
  } else {
    let maxDsq = 0;
    for (let i = 0; i < W * H; i++) {
      if (dsq[i] > maxDsq && dsq[i] < EDT_INF) maxDsq = dsq[i];
    }
    maxDistPx = Math.sqrt(maxDsq);
    if (dome) dome.measuredPx = maxDistPx;
  }
  const bandPx = maxDistPx > 1 ? Math.min(paramBandPx, maxDistPx) : paramBandPx;
  const chisel = p.technique === 'chisel';
  const height = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) {
    const t = Math.min(1, Math.sqrt(dsq[i]) / bandPx);
    height[i] = chisel ? t : 1 - (1 - t) * (1 - t);
  }
  if (!chisel) {
    // DOME LAW (v4, 2026-07-19 — owner: "rounded... more like a domed
    // sticker and there should be no hard edges"): the quarter-round profile
    // is C¹ along each flank but the EDT field itself creases wherever two
    // edges' distances tie — the stroke's medial axis and the mitre rays at
    // junctions. Height stays continuous there while the gradient DIRECTION
    // flips, and the Sobel turns that into a hard crest line — which is why
    // v3's Rounded read like Chiseled's softer sibling instead of a dome. A
    // resin dome has no such crease: its curvature smooths OVER the spine.
    // So Rounded now blurs the HEIGHT FIELD itself at σ ∝ the effective band
    // (see DOME_SMOOTH_K for the tuning + the plateau-law reconciliation).
    // Chiseled is untouched — its facets and mitre ridge ARE the look.
    const domeSigma = DOME_SMOOTH_K * bandPx;
    if (domeSigma > 0.4) boxBlurField(height, W, H, domeSigma);
  }
  // Finish shoulder: matte widens (softer), metal tightens — added to the
  // caller's Soften, floored at 0 (satin adds 0 → byte-identical).
  const softenEff = Math.max(0, p.soften + fin.softenAdd);
  const softPx = Math.min(bandPx * 0.45, Math.max(0.6, softenEff * 0.045 * unitPx));
  blurField(height, W, H, softPx);

  // Apparent height — depth = steepness, never band width. A and bandPx both
  // scale with unitPx → the SAME look at every zoom / quality tier. Rides
  // the EFFECTIVE band: at the width-saturation clamp A ≈ band gives the
  // hemisphere ratio (the balloon), instead of an over-tall cone.
  const A = bandPx * (0.30 + (p.depth / 100) * 1.20);

  const az = (light.azDeg * Math.PI) / 180;
  const alt = (light.altDeg * Math.PI) / 180;
  const altSh = alt * SHADE_ALT_K; // diffuse sun
  // Canvas y is DOWN — flip Ly so "light from top-left" reads intuitively.
  const Lx = Math.cos(altSh) * Math.cos(az);
  const Ly = -Math.cos(altSh) * Math.sin(az);
  const Lz = Math.sin(altSh);
  // Blinn half-vector — TRUE altitude.
  const Sx = Math.cos(alt) * Math.cos(az);
  const Sy = -Math.cos(alt) * Math.sin(az);
  const Sz = Math.sin(alt);
  const hLen = Math.hypot(Sx, Sy, Sz + 1) || 1;
  const Hxv = Sx / hLen, Hyv = Sy / hLen, Hzv = (Sz + 1) / hLen;
  // Specular gloss + highlight/shadow scale come from the finish (satin =
  // gloss 30, K 1 → byte-identical to the pre-finish law). hiO/shO clamped ≤1
  // so a boosted term (glossy/metal K>1) can't invert the screen/multiply.
  const glossAmt = fin.gloss / 100;
  const specExp = 8 + glossAmt * 36;
  const specFace = Math.pow(Math.max(0, Hzv), specExp);
  const specNorm = 1 - specFace > 1e-3 ? 1 / (1 - specFace) : 0;
  const hiO = Math.min(1, (p.highlight / 100) * fin.hiK);
  const shO = Math.min(1, (p.shadow / 100) * fin.shK);
  const invHiRange = 1 / Math.max(1e-3, 1 - Lz);
  const invShRange = 1 / (1 + Lz);
  const shAmb = (1 - AMBIENT) * shO;

  // Contact shadow: sample the blurred silhouette TOWARD the light — the
  // shadow lands on the side away from it. Subtle at defaults.
  const lxyLen = Math.hypot(Lx, Ly) || 1;
  const offPx = (1.5 + p.size * 0.08) * unitPx * Math.cos(alt);
  const offX = (Lx / lxyLen) * offPx;
  const offY = (Ly / lxyLen) * offPx;
  const contactO = 0.30 * shO * (0.35 + 0.65 * (p.depth / 100));

  const idx = (x, y) => Math.min(H - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x));

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const i = y * W + x;
      const o = i * 4;
      const alpha = bd[o + 3];
      const la = mask ? mask[i] : alpha;

      if (la === 0) {
        // Not this layer's pixel. Writes gated by the under-law (layer doc):
        // maskless renders take exactly the pre-lane branch.
        if (alpha === 0) {
          // Fully transparent: the classic outside contact-shadow write.
          if (under === 'shadow' && cd && contactO > 0.004) {
            const sa = cd[idx(Math.round(x + offX), Math.round(y + offY)) * 4 + 3] / 255;
            const outA = sa * contactO;
            if (outA > 0.004) {
              bd[o] = 0; bd[o + 1] = 0; bd[o + 2] = 0;
              bd[o + 3] = Math.round(Math.min(1, outA) * 255);
            }
          }
        } else if (mask && under === 'shadow' && cd && contactO > 0.004) {
          // Base ink BENEATH the layer (sticker halo/badge under the domed
          // letters): the letters' contact shadow lands as a straight
          // multiply — the soft grounding that sells the two-layer read.
          const sa = cd[idx(Math.round(x + offX), Math.round(y + offY)) * 4 + 3] / 255;
          const outA = sa * contactO;
          if (outA > 0.004) {
            const k = 1 - Math.min(1, outA);
            bd[o] = Math.round(bd[o] * k);
            bd[o + 1] = Math.round(bd[o + 1] * k);
            bd[o + 2] = Math.round(bd[o + 2] * k);
          }
        }
        continue;
      }
      // Mask over transparent base (AA fringe at a layer edge): no paint to
      // shade — and the outside write must not run either.
      if (alpha === 0) continue;

      // Sobel gradient of the height field, clamp-to-edge (a glyph mask,
      // unlike a seamless tile, must not wrap).
      const hL = height[idx(x - 1, y)];
      const hR = height[idx(x + 1, y)];
      const hU = height[idx(x, y - 1)];
      const hD = height[idx(x, y + 1)];
      const hUL = height[idx(x - 1, y - 1)];
      const hUR = height[idx(x + 1, y - 1)];
      const hDL = height[idx(x - 1, y + 1)];
      const hDR = height[idx(x + 1, y + 1)];
      const gx = (hUR + 2 * hR + hDR - (hUL + 2 * hL + hDL)) / 4;
      const gy = (hDL + 2 * hD + hDR - (hUL + 2 * hU + hUR)) / 4;
      const nx = -gx * A;
      const ny = -gy * A;
      const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
      const ndl = (nx * Lx + ny * Ly + Lz) * inv;

      // Face-relative terms — the plateau (ndl = Lz) is the zero point: the
      // face keeps the paint untouched, rims swing bright/dark.
      const dev = ndl - Lz;
      const hi = dev > 0 ? Math.pow(Math.min(1, dev * invHiRange), 0.75) * hiO : 0;
      const sh = dev < 0 ? Math.pow(Math.min(1, -dev * invShRange), 0.85) * shAmb : 0;
      const ndh = (nx * Hxv + ny * Hyv + Hzv) * inv;
      const spec = ndh > 0
        ? Math.max(0, (Math.pow(ndh, specExp) - specFace) * specNorm) * glossAmt * 0.9 * hiO
        : 0;

      for (let c = 0; c < 3; c++) {
        let v = bd[o + c] / 255;
        v = 1 - (1 - v) * (1 - hi); // screen toward white highlight
        v *= 1 - sh; // multiply toward black shadow
        v = 1 - (1 - v) * (1 - spec); // gloss bloom
        bd[o + c] = Math.round(Math.min(1, Math.max(0, v)) * 255);
      }
    }
  }
}

registerFx({
  id: 'bevel',
  // v2: EDT plateau band + face-relative light (embossRecipe v3).
  // v3 (2026-07-19, light-shape-finish lane): WIDTH-SATURATION — the
  // effective band clamps at the glyph's max interior distance (and the
  // apparent height follows it), so a maxed "Bevel width" reads as a true
  // dome/pyramid instead of a saturated cone. Same-params renders change
  // ONLY where the old band exceeded that distance — a regime no shipped UI
  // or preset could store (the sidebar never exposed size before this lane;
  // saved live bevels all carry the default 34, far below the clamp on any
  // real headline). technique 'chisel' + lightMode 'poster' also landed here
  // — both default-compatible (old params sanitize to smooth/custom).
  // v4 (2026-07-19, dome-parity lane): TRUE DOME — Rounded blurs the height
  // field at σ ∝ the effective band (DOME_SMOOTH_K), erasing the medial-axis
  // crest and junction mitre creases the EDT bakes into every profile, so
  // Rounded reads as a puffy domed sticker at any width and is finally
  // OBVIOUSLY different from Chiseled (owner: the two "are almost identical").
  // Every smooth render softens a little (a few px at the default width 34 —
  // deliberately: "no hard edges" applies at every width); chisel renders
  // change only where softPx > 8 (blurField now honors large σ via box
  // passes instead of silently truncating at its radius-24 kernel cap).
  // v5 (2026-07-20, sticker-shapes/layer-separation lane): LETTERS ONLY when
  // a sticker/outline band is live — height, shading and the contact
  // silhouette derive from the seam's stroke-suppressed variant pass, the
  // band/badge beneath renders flat and receives the letters' contact
  // shadow (owner: "the standard bevel should be applied to the text").
  // Same-params renders change ONLY for objects carrying such a band
  // (env.renderVariant absent ⇒ bit-for-bit the v4 path).
  // 2026-07-20 (finish/edge-law lane): `finish` param added (surface family —
  // satin default = the pre-finish constants, so same-params renders are
  // byte-identical; matte/glossy/metal are the new looks, anchored to the
  // baked style bundles — see FINISHES) and the layer contract gained an
  // optional `heightMask` (height source ≠ paint mask). No version bump — old
  // params default to satin and omit heightMask, exactly the fxExtrude
  // 'poster' precedent (additive, default-compatible).
  version: 5,
  kind: 'filter',
  sanitize,
  isEmpty: (p) => !(p.depth > 0),
  spill,
  applySync: (canvas, env) => runBevel(canvas, env, env.quality === 'full' ? 1 : 0.5),
  applyAsync: async (canvas, env) => runBevel(canvas, env, 1),
});

// ---------------------------------------------------------------------------
// Shared shading core (sticker-shapes lane, 2026-07-20). fxStickerShape domes
// its badge/band layer through the EXACT pipeline above — same EDT, same
// profiles, same width-saturation and dome laws, same face-relative light —
// by calling shadeBevelCore with a layer mask. Exported here (not copied)
// because BOTH consumers are live-fx modules with one look to keep in sync;
// the copy-not-import rule at the top of this file is about embossRecipe's
// byte-guarded BAKED engine, which stays untouched.
//   shadeBevelCore(img, cSrc, W, H, params, unitPx, light, dome, layer) —
//     see shadeBevel's JSDoc for every argument's contract.
//   BEVEL_ALTITUDE_DEG — the hidden default sun altitude, so a caller
//     building its own {azDeg, altDeg} light matches the house look.
export { shadeBevel as shadeBevelCore, ALTITUDE_DEG as BEVEL_ALTITUDE_DEG };
